import React, {useEffect} from "react";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../../../redux/store";
import {
    deletePrevisione,
    getPrevisione,
    Previsione,
    savePrevisione,
    setPrevisioneCorrenteId,
} from "../previsioniSlice";
import {ErrorStatusTypes} from "components/common/errorsDeclarations";
import {Routes_path} from "components/routerApp";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import {styled} from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PrevisioniPageBreadcrumbs from "../PrevisioniPageBreadcrumbs";
import PrevisioneForm from "./PrevisioneForm";
import AllegatiPage from "../../../../allegati/AllegatiPage";
import OrdiniLavoroCard from "./ordiniLavoro/OrdiniLavoroCard";
import {useAutorizzazioneGruppi} from "../../../../authentication/useAutorizzazioneGruppi";
import {PermissionTypes} from "../../../../authentication/permissionsGroups";
import AllegatiPreview from "../../../../allegati/AllegatiPreview";

// ============================================
// STYLED COMPONENTS
// ============================================

const PageContainer = styled(Box)(({theme}) => ({
    padding: theme.spacing(3),
    margin: "0 auto",
}));

const HeaderCard = styled(Card)(({theme}) => ({
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
}));

const FormCard = styled(Card)(({theme}) => ({
    marginBottom: theme.spacing(2),
}));

// ============================================
// COMPONENT
// ============================================

const PrevisionePage = () => {
    const {previsione_id} = useParams<{ previsione_id: string }>();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    // Determina se siamo in creazione o modifica
    const isCreating = !previsione_id;
    const previsioneId = previsione_id ? parseInt(previsione_id, 10) : undefined;

    // ============================================
    // SELECTORS
    // ============================================

    const consentiModificaPianificatori = useAutorizzazioneGruppi([PermissionTypes.PIANIFICATORI, PermissionTypes.COMMERCIALI, PermissionTypes.GESTORI_SISTEMA])
    const consentiModificaOperatori = useAutorizzazioneGruppi([PermissionTypes.PIANIFICATORI, PermissionTypes.COMMERCIALI, PermissionTypes.GESTORI_SISTEMA, PermissionTypes.SCHEDULATORI])

    const previsioneCorrente = useSelector<RootState, Previsione | undefined>(
        (state) => state.previsioniState.previsioniList.results.find(
            (item) => item.id === state.previsioniState.previsioneCorrenteId
        )
    );

    const previsioniStatus = useSelector<RootState, ErrorStatusTypes>(
        (state) => state.previsioniState.previsioniList.errorsStack.status
    );

    const location = useLocation();
    type LocationState = {
        stagione?: number;
        commerciale?: number;
        mandatario?: number;
        brand?: number;
        confezionista?: number;
    };

    // Previsione vuota per creazione
    const previsioneVuota: Previsione = {
        stagione: (location.state as LocationState)?.stagione || undefined,
        commerciale: (location.state as LocationState)?.commerciale || undefined,
        mandatario: (location.state as LocationState)?.mandatario || undefined,
        brand: (location.state as LocationState)?.brand || undefined,
        confezionista: (location.state as LocationState)?.confezionista || undefined,
        unita_misura: "PZ",
        fase_collezione: "PROT",
    };

    // ============================================
    // EFFECTS
    // ============================================

    // Carica previsione se in modifica
    useEffect(() => {
        if (previsioneId) {
            dispatch(setPrevisioneCorrenteId(previsioneId));
            dispatch(getPrevisione({previsione_id: previsioneId}));
        } else {
            // Reset per nuova previsione
            dispatch(setPrevisioneCorrenteId(undefined));
        }
    }, [dispatch, previsioneId]);

    // ============================================
    // HANDLERS
    // ============================================

    const handleBack = () => {
        navigate(Routes_path.PREVISIONI);
    };

    const handleDelete = async () => {
        if (!previsioneCorrente) return;

        const confirmMessage = `Sei sicuro di voler eliminare la previsione ${previsioneCorrente.codice_item || 'questa previsione'}?`;

        if (window.confirm(confirmMessage)) {
            await dispatch(deletePrevisione({previsioneToDelete: previsioneCorrente}));
            navigate(Routes_path.PREVISIONI);
        }
    };

    const handleDuplicate = async () => {
        if (!previsioneCorrente) return;
        if (!consentiModificaPianificatori) return;

        // Crea una copia senza ID per duplicazione
        // const previsioneDuplicata: Previsione = {
        //     ...previsioneCorrente,
        //     id: undefined,
        //     codice_item: `${previsioneCorrente.codice_item}_COPIA`,
        //     descrizione_item: `${previsioneCorrente.descrizione_item} (COPIA)`,
        // };

        // Naviga alla creazione con i dati precompilati
        // Questo richiederà una gestione particolare nel form
        const result = await  dispatch(savePrevisione({previsioneToSave: previsioneCorrente, azione: 'duplica'}));
        // navigate(Routes_path.PREVISIONE);
        if (result.meta.requestStatus === 'fulfilled') {
            const newId = (result.payload as Previsione).id;
            navigate(`${Routes_path.PREVISIONE}${newId}`);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    // Determina quale previsione usare
    const previsione = isCreating ? previsioneVuota : (previsioneCorrente || previsioneVuota);

    // Determina se permettere modifiche

    // ============================================
    // RENDER
    // ============================================

    return (
        <PageContainer>
            {/* Breadcrumbs */}
            <Box sx={{mb: 2}}>
                <PrevisioniPageBreadcrumbs/>
            </Box>

            {/* Header con azioni */}
            <HeaderCard>
                <CardHeader
                    title={
                        <Grid container alignItems="center" spacing={2}>
                            <Grid size={{xs: 12, sm: 6}}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <IconButton onClick={handleBack} size="small">
                                        <ArrowBackIcon/>
                                    </IconButton>
                                    <Typography variant="h5">
                                        {isCreating ? "Nuovo Listino" : `Listino: ${previsione.codice_item || '#' + previsione.id}`}
                                    </Typography>
                                </Stack>
                            </Grid>
                            <Grid size={{xs: 12, sm: 6}}>
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    {!isCreating && consentiModificaPianificatori && (
                                        <>
                                            <Button
                                                startIcon={<ContentCopyIcon/>}
                                                onClick={handleDuplicate}
                                                variant="outlined"
                                                size="small"
                                            >
                                                Duplica
                                            </Button>
                                            <Button
                                                startIcon={<DeleteIcon/>}
                                                onClick={handleDelete}
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                            >
                                                Elimina
                                            </Button>
                                        </>
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>
                    }
                />
            </HeaderCard>

            {/* Alert per errori generali */}
            {previsione?.errorsStack?.fieldsErrors?.non_field_errors && (
                <Alert severity="error" sx={{mb: 2}}>
                    {previsione.errorsStack.fieldsErrors.non_field_errors.toString()}
                </Alert>
            )}

            {/* Form principale */}
            <Grid container spacing={2}>
                <Grid size={{xs: 12, sm: 8}}>
                    <Card>
                        <CardContent>
                            {/* Form */}
                            <PrevisioneForm
                                previsione={previsione}
                                consentiModifica={consentiModificaPianificatori}
                                isCreating={isCreating}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{xs: 12, sm: 4}}>

                    {previsione.id &&
                        <AllegatiPreview key={`AllegatiPreview_${previsione.id}`} previsione_id={previsione.id}/>
                    }

                    {previsione.id &&
                        <AllegatiPage key={previsione.id} previsione_id={previsione.id}
                                      consentiModifica={consentiModificaOperatori}/>
                    }
                </Grid>
                {['CAMP'].includes(previsione.fase_collezione || '-') &&
                    <Grid size={{xs: 12,}}>
                        {/*<OrdiniLavoroCard previsione={previsione} consentiModifica={true}/>*/}
                        <OrdiniLavoroCard previsione={previsione} consentiModifica={consentiModificaOperatori}/>
                    </Grid>
                }
            </Grid>

            {/* Loading overlay */}
            <Backdrop
                open={previsioniStatus === ErrorStatusTypes.PENDING}
                sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}
            >
                <CircularProgress color="inherit"/>
            </Backdrop>
        </PageContainer>
    );
};

export default PrevisionePage;